// "My Rankings" / "Rankings" on a profile, between Top Movies and Recently
// Rated: up to three newest rankings with a poster strip, Top N and type.
//
// Owner (MyProfile): published rankings and drafts (drafts are labelled, with
// their progress; published ones with private edits say so),
// Create / See All, an empty state with Create, and a retry on error. Each
// row opens the editor. Visitors only ever receive PUBLISHED rankings (their
// published version), and only
// when the profile itself is viewable (GET /users/:username/rankings applies
// the profile's own canView rule) — so nothing private can reach this
// component; no rankings means the section is hidden. Rows open the public
// page, which has Make Your Own.
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import topStyles from '../TopMovies/TopMovies.module.css';
import { rankingProgressLabel } from '@aluvo/constants/src/rankings';
import styles from './RankingsProfileSection.module.css';
import { PROFILE_RANKINGS_LIMIT } from './useProfileRankings';

export { PROFILE_RANKINGS_LIMIT };
const COMPOSITION_LABEL = { movie: 'Movies', tv: 'TV', mixed: 'Movies & TV' };

function RankingRow({ ranking, isOwner }) {
    const to = isOwner ? `/rankings/${ranking.id}/edit` : `/rankings/${ranking.shareSlug}`;
    const isDraft = isOwner && (ranking.status ? ranking.status !== 'published' : ranking.visibility !== 'public');
    const title = ranking.title || 'Untitled draft';
    const progress = isDraft && ranking.itemCount !== undefined && ranking.itemCount < ranking.size ? rankingProgressLabel(ranking.itemCount, ranking.size) : null;
    const meta = [`Top ${ranking.size}`, COMPOSITION_LABEL[ranking.composition], progress].filter(Boolean).join(' · ');
    const changes = isOwner && !isDraft && ranking.hasUnpublishedChanges;
    return (
        <li>
            <Link
                to={to}
                className={styles.row}
                aria-label={`${title}, ${meta}${isDraft ? ', draft' : ''}${changes ? ', unpublished changes' : ''}${isOwner ? '. Edit' : ''}`}
            >
                <span className={styles.posters} aria-hidden="true">
                    {(ranking.previewPosters || []).slice(0, 4).map((url) => <img key={url} src={url} alt="" loading="lazy" />)}
                    {!ranking.previewPosters?.length && <span className={styles.noPoster}>🏆</span>}
                </span>
                <span className={styles.text}>
                    <strong>{title}</strong>
                    <span className={styles.meta}>
                        {meta}
                        {isDraft && <span className={styles.private}>Draft</span>}
                        {changes && <span className={styles.private}>Unpublished changes</span>}
                    </span>
                </span>
            </Link>
        </li>
    );
}

/**
 * @param props.items     undefined while loading, null when hidden, else the first page
 * @param props.hasMore   more rankings than shown (visitors: whether to offer See All)
 * @param props.isOwner
 * @param props.seeAllTo  /me/rankings for the owner, /user/:username/rankings for visitors
 * @param props.error / onRetry   owner fetch failed
 * @param props.sourceScreen      analytics: my_profile | profile | public_profile
 * @param props.loggedIn
 */
export default function RankingsProfileSection({
    items, hasMore = false, isOwner = false, seeAllTo, error = false, onRetry, sourceScreen, loggedIn = true, className = '',
}) {
    const profileType = isOwner ? 'own' : 'other';
    const count = Array.isArray(items) ? Math.min(items.length, PROFILE_RANKINGS_LIMIT) : 0;
    const viewedRef = useRef(false);

    // Once per profile view, only when rankings actually show.
    useEffect(() => {
        if (viewedRef.current || count === 0) return;
        viewedRef.current = true;
        trackEvent(ANALYTICS_EVENTS.RANKINGS_PROFILE_VIEWED, { source_screen: sourceScreen, profile_type: profileType, ranking_count: count, has_more: hasMore, logged_in: loggedIn });
    }, [count, hasMore, sourceScreen, profileType, loggedIn]);

    const heading = isOwner ? 'My Rankings' : 'Rankings';
    const create = (label, primary = false) => (
        <Link to="/rankings/new" state={{ source: 'profile' }} className={primary ? topStyles.primary : topStyles.secondary}>{label}</Link>
    );

    if (error && isOwner) {
        return (
            <section className={`${topStyles.profileCard} ${className}`} aria-label={heading}>
                <span className={topStyles.kicker}>{heading}</span>
                <p className={styles.errorText} role="alert">
                    Couldn&apos;t load your rankings.{' '}
                    {onRetry && <button type="button" className={styles.linkBtn} onClick={onRetry}>Try again</button>}
                </p>
            </section>
        );
    }
    if (items === undefined || items === null) return null;

    if (count === 0) {
        if (!isOwner) return null;
        return (
            <section className={`${topStyles.profileCard} ${topStyles.emptyCard} ${className}`} aria-label={heading}>
                <span className={topStyles.kicker}>{heading} 🏆</span>
                <h2 style={{ margin: 0 }}>Rank anything you love.</h2>
                <p className={styles.emptyText}>Your Top 5 or Top 10 — movies, TV shows or both. Share it and see who makes their own.</p>
                {create('Create a Ranking', true)}
            </section>
        );
    }

    const showSeeAll = Boolean(seeAllTo) && (isOwner || hasMore);
    return (
        <section className={`${topStyles.profileCard} ${className}`} aria-label={heading}>
            <div className={topStyles.profileHead}>
                <h2>{heading}</h2>
                {showSeeAll && (
                    <Link
                        to={seeAllTo}
                        className={styles.seeAll}
                        onClick={() => trackEvent(ANALYTICS_EVENTS.RANKINGS_SEE_ALL_CLICKED, { source_screen: sourceScreen, profile_type: profileType, logged_in: loggedIn })}
                    >
                        See All Rankings →
                    </Link>
                )}
            </div>
            <ul className={styles.list}>
                {dedupe(items).slice(0, PROFILE_RANKINGS_LIMIT).map((ranking) => (
                    <RankingRow key={rankingKey(ranking)} ranking={ranking} isOwner={isOwner} />
                ))}
            </ul>
            {isOwner && <div className={styles.actions}>{create('+ Create a Ranking')}</div>}
        </section>
    );
}

const rankingKey = (ranking) => ranking.id || ranking.shareSlug;

/** Never render the same ranking twice (e.g. a page boundary shifting after an edit). */
function dedupe(items) {
    const seen = new Set();
    return items.filter((ranking) => {
        const key = rankingKey(ranking);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}
